import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Paper, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { buildEventsApiUrl } from '../config/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const code = useMemo(() => params.get('code') || '', [params]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [valid, setValid] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkCode = async () => {
      if (!code) {
        setError('Missing reset code.');
        setChecking(false);
        return;
      }

      try {
        const response = await fetch(buildEventsApiUrl(`/api/auth/forgot-password-status?code=${encodeURIComponent(code)}`));
        const result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Reset link is invalid');
        }
        setValid(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Reset link is invalid');
      } finally {
        setChecking(false);
      }
    };

    checkCode();
  }, [code]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setMessage(null);
      const response = await fetch(buildEventsApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, password })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to reset password');
      }

      setMessage(result.message || 'Password reset successfully.');
      setValid(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper sx={{ p: 4 }} elevation={3}>
        <Typography variant="h4" gutterBottom>Reset Password</Typography>

        {checking ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {valid && (
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  required
                />
                <Button type="submit" fullWidth variant="contained" disabled={submitting} sx={{ mt: 2 }}>
                  {submitting ? <CircularProgress size={24} /> : 'Reset Password'}
                </Button>
              </Box>
            )}

            <Button component={RouterLink} to="/login" sx={{ mt: 2 }}>
              Back to Login
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );
}
